import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import ChannelAppResource from './ChannelAppResource';
import OrganizationAppResource from './OrganizationAppResource';
import PropertyAppResource from './PropertyAppResource';
import StageTypeAppResource from './StageTypeAppResource';
import TaskAssigneeAppResource from './TaskAssigneeAppResource';
import TaskCategoryAppResource from './TaskCategoryAppResource';
import TaskPriorityAppResource from './TaskPriorityAppResource';
import TaskStatusAppResource from './TaskStatusAppResource';
import WorkspaceAppResource from './WorkspaceAppResource';
import WorkspaceOrganizationMemberAppResource from './WorkspaceOrganizationMemberAppResource';
import TaskReminderAppResource from './TaskReminderAppResource';
import FileAppResource from './FileAppResource';
import TaskListAppResource from "./TaskListAppResource";
import KnowledgeAssetArticleAppResource from "./KnowledgeAssetArticleAppResource";
import KnowledgeAssetVideoAppResource from "./KnowledgeAssetVideoAppResource";
import DateTimeHelper from "../Helpers/DateTimeHelper";
import CalendarEventAppResource from "./CalendarEventAppResource";
import LuWorkspaceMembershipTypeAppResource from './LuWorkspaceMembershipTypeAppResource';
import ResourceAppResource from "./ResourceAppResource";
import TaskRecurrenceAppResource from "./TaskRecurrenceAppResource";
import KnowledgeAssetAppResource from "./KnowledgeAssetAppResource";
import isEmpty from "lodash/isEmpty";
import Engine from "@milestoneslabs/milestones-js-engine/src/Engine";
class TaskAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'task';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'tasks';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'title',
            'description',
            'deadline',
            'completedDate',
            'starred',
            'taskCategoryUuid',
            'taskPriorityUuid',
            'taskStatusUuid',
            'parentTaskUuid',
            'organizationUuid',
            'workspaceUuid',
            'ownerWorkspaceOrganizationMemberUuid',
            'stageTypeUuid',
            'propertyUuid',
            'channelUuid',
            'taskListUuid',
            'meta',
            'taskCategory',
            'taskPriority',
            'taskStatus',
            'parentTask',
            'organization',
            'workspace',
            'ownerWorkspaceOrganizationMember',
            'stageType',
            'property',
            'channel',
            'childrenTasks',
            'assignees',
            'reminders',
            'files',
            'order',
            'assets',
            'videoAssets',
            'deadlineParentCalendarEventUuid',
            'deadlineParentTaskUuid',
            'differenceBetweenDates',
            'deadlineParentEvent',
            'deadlineParentTask',
            'assignedRoles',
            'formsetResources',
            'serviceProviderResources',
            'recurrences',
            'knowledgeAssets',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            taskCategory: TaskCategoryAppResource,
            taskPriority: TaskPriorityAppResource,
            taskStatus: TaskStatusAppResource,
            parentTask: TaskAppResource,
            organization: OrganizationAppResource,
            workspace: WorkspaceAppResource,
            ownerWorkspaceOrganizationMember: WorkspaceOrganizationMemberAppResource,
            stageType: StageTypeAppResource,
            property: PropertyAppResource,
            channel: ChannelAppResource,
            childrenTasks: TaskAppResource,
            assignees: TaskAssigneeAppResource,
            reminders: TaskReminderAppResource,
            files: FileAppResource,
            taskList: TaskListAppResource,
            assets: KnowledgeAssetArticleAppResource,
            videoAssets: KnowledgeAssetVideoAppResource,
            formsetResources: ResourceAppResource,
            serviceProviderResources: ResourceAppResource,
            deadlineParentEvent: CalendarEventAppResource,
            deadlineParentTask: TaskAppResource,
            deadlineChildrenEvents: CalendarEventAppResource,
            deadlineChildrenTasks: TaskAppResource,
            assignedRoles: LuWorkspaceMembershipTypeAppResource,
            recurrences: TaskRecurrenceAppResource,
            recurringChildren: TaskAppResource,
            knowledgeAssets: KnowledgeAssetAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            taskCategory: 'task_category_uuid',
            taskPriority: 'task_priority_uuid',
            taskStatus: 'task_status_uuid',
            parentTask: 'parent_task_uuid',
            organization: 'organization_uuid',
            workspace: 'workspace_uuid',
            ownerWorkspaceOrganizationMember: 'owner_workspace_organization_member_uuid',
            stageType: 'stage_type_uuid',
            property: 'property_uuid',
            channel: 'channel_uuid',
            taskList: 'task_list_uuid',
            deadlineParentEvent: 'deadline_parent_calendar_event_uuid',
            deadlineParentTask: 'deadline_parent_task_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            title: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            description: {
                type: 'string',
            },
            starred: {
                type: 'boolean',
            },
            deadline: {
                datetime: true,
            },
            completedDate: {
                datetime: true,
            },
            organizationUuid: {
                type: 'string',
            },
            workspaceUuid: {
                type: 'string',
            },
            ownerWorkspaceOrganizationMemberUuid: {
                type: 'string',
            },
            taskCategoryUuid: {
                type: 'string',
            },
            taskPriorityUuid: {
                type: 'string',
            },
            taskStatusUuid: {
                type: 'string',
            },
            parentTaskUuid: {
                type: 'string',
            },
            stageTypeUuid: {
                type: 'string',
            },
            propertyUuid: {
                type: 'string',
            },
            channelUuid: {
                type: 'string',
            },
            taskListUuid: {
                type: 'string',
            },
            order: {
                type: 'integer',
            },
            deadlineParentCalendarEventUuid: {
                type: 'string',
            },
            deadlineParentTaskUuid: {
                type: 'string',
            },
            differenceBetweenDates: {
                type: 'integer',
            },
        };
    }

    /**
     * @returns {{deadline: string}}
     */
    getAPIDatetimeFormat() {
        return {
            deadline: DateTimeHelper.apiDateTimeFormat,
            completedDate: DateTimeHelper.apiDateTimeFormat
        }
    }

    apiPaths() {
        return {
            ...super.apiPaths(),
            'createRecurringChild': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/create-recurring-child'),
            'updateRecurringChild': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/update-recurring-child'),
            'deleteRecurringChild': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/delete-recurring-child'),
        };
    }

    async createRecurringChild(shiftedDueDate) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        if (isEmpty(shiftedDueDate)) {
            throw Error('Invalid due date.');
        }

        const url = this.apiPaths().createRecurringChild.replace('{uuid}', this.uuid);

        return await this.enginePost(url, {
            shiftedDueDate: shiftedDueDate.format(this.getAPIDatetimeFormat().deadline),
        });
    }

    async updateRecurringChild(shiftedDueDate) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        if (isEmpty(shiftedDueDate)) {
            throw Error('Invalid due date.');
        }

        const url = this.apiPaths().updateRecurringChild.replace('{uuid}', this.uuid);

        return await this.enginePost(url, {
            shiftedDueDate: shiftedDueDate.format(this.getAPIDatetimeFormat().deadline),
        });
    }

    async deleteRecurringChild() {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        const url = this.apiPaths().deleteRecurringChild.replace('{uuid}', this.uuid);

        return await this.enginePost(url);
    }
}

export default TaskAppResource;
